import { useMemo, useState } from 'react'
import { useFirestoreQuery } from '../../firebase/useFirestore'
import usePermissions from '../usePermissions'
import summarizeProjectNotes from '../../helpers/portfolio/projectNotesSummary'
import ProjectNoteSummary from '../../components/portfolio/ProjectNoteSummary'
import ProjectNoteModal from '../../components/portfolio/projectNotes/ProjectNoteModal'

/**
 * @typedef {{CompagnyID: string, CommunityName?: string}} NoteProject
 */

/**
 * De kolom NOTITIES van het projectenoverzicht en de kaart 'Projecten met
 * ster': de projectnotities van deze omgeving (één listener), de cel per
 * project met '+ Notitie', en het venster dat die knop opent. Nodig omdat
 * beide schermen precies hetzelfde recept hadden (review /complete 2026-10-16);
 * het tabblad 'Notities' op de projectpagina leest per project en gebruikt
 * alleen het venster.
 *
 * Projectnotities bestaan alleen in de beheeromgeving: met `enabled` false
 * wordt er niets gelezen en is er geen knop.
 * AI-CONTEXT: See docs/features/deccos-projectafspraken.md
 * AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
 *
 * @param {boolean} enabled - Of deze omgeving projectnotities heeft (de beheertenant)
 * @returns {{renderNoteCell: (item: NoteProject) => JSX.Element, noteModal: JSX.Element | null}}
 */
const useProjectNotesColumn = (enabled) => {
    // Mislukt het lezen, dan zegt de cel dat; anders bleef hij eeuwig op '…'.
    const [notesFailed, setNotesFailed] = useState(false)
    const projectNotes = useFirestoreQuery('ProjectNotes', { enabled, onError: () => setNotesFailed(true) })
    const notesLoaded = Array.isArray(projectNotes)
    const notesByProject = useMemo(() => summarizeProjectNotes(projectNotes), [projectNotes])

    // Zelfde recht als het tabblad 'Notities' op de projectpagina.
    const { has, ready } = usePermissions()
    const canAddNote = enabled && ready && has('tasks.edit')
    const [noteProject, setNoteProject] = useState(/** @type {NoteProject | null} */ (null))

    /** @param {NoteProject} item */
    const renderNoteCell = (item) => (
        <ProjectNoteSummary
            summary={notesByProject.get(item.CompagnyID)}
            loaded={notesLoaded}
            failed={notesFailed}
            projectName={item.CommunityName || item.CompagnyID}
            onAdd={canAddNote ? () => setNoteProject(item) : undefined}
        />
    )

    const noteModal = noteProject ? (
        <ProjectNoteModal
            modalOpen={true}
            setModalOpen={(open) => { if (!open) setNoteProject(null) }}
            projectId={noteProject.CompagnyID}
            projectName={noteProject.CommunityName || noteProject.CompagnyID}
            position={(notesByProject.get(noteProject.CompagnyID)?.count || 0) + 1}
        />
    ) : null

    return { renderNoteCell, noteModal }
}

export default useProjectNotesColumn
